/**
 * FSANZ (AUSNUT/AFCD) food database: search and nutrient lookup.
 * Data is loaded from a JSON file (sample bundled; replace with full AUSNUT export for production).
 */

import type { FSANZFood, MealNutrients, NutrientsPer100g } from './fsanz-types'
import { nutrientsForAmount } from './fsanz-types'
import { getScannedFoods } from './scannedProductsCache'

// Sample foods (AUSNUT-style names and typical per-100g values). Replace with full DB load.
const SAMPLE_FOODS: FSANZFood[] = [
  {
    id: 'f-1',
    name: 'Banana, raw',
    shortName: 'Banana',
    per100g: { energyKcal: 89, proteinG: 1.1, carbsG: 22.8, fibreG: 2.6, calciumMg: 5, ironMg: 0.3, waterG: 75 },
  },
  {
    id: 'f-2',
    name: 'Bread, wholemeal, commercially prepared',
    shortName: 'Wholemeal bread',
    per100g: { energyKcal: 247, proteinG: 9.4, carbsG: 41.4, fibreG: 6.9, calciumMg: 106, ironMg: 2.4, waterG: 38 },
  },
  {
    id: 'f-3',
    name: 'Oats, rolled, uncooked',
    shortName: 'Rolled oats',
    per100g: { energyKcal: 389, proteinG: 16.9, carbsG: 66.3, fibreG: 10.6, calciumMg: 54, ironMg: 4.7, waterG: 8.2 },
  },
  {
    id: 'f-4',
    name: 'Egg, chicken, whole, raw',
    shortName: 'Egg, whole',
    per100g: { energyKcal: 143, proteinG: 12.6, carbsG: 0.7, fibreG: 0, calciumMg: 56, ironMg: 1.8, waterG: 76, vitaminDUg: 1.8 },
  },
  {
    id: 'f-5',
    name: 'Milk, cow, regular, whole, pasteurised',
    shortName: 'Whole milk',
    per100g: { energyKcal: 61, proteinG: 3.2, carbsG: 4.8, fibreG: 0, calciumMg: 113, ironMg: 0.03, waterG: 88 },
  },
  {
    id: 'f-6',
    name: 'Rice, white, long-grain, cooked, unenriched',
    shortName: 'White rice, cooked',
    per100g: { energyKcal: 130, proteinG: 2.7, carbsG: 28.2, fibreG: 0.4, calciumMg: 10, ironMg: 0.2, waterG: 68 },
  },
  {
    id: 'f-7',
    name: 'Chicken, breast, flesh only, raw',
    shortName: 'Chicken breast',
    per100g: { energyKcal: 114, proteinG: 23.1, carbsG: 0, fibreG: 0, calciumMg: 5, ironMg: 0.4, waterG: 75 },
  },
  {
    id: 'f-8',
    name: 'Yoghurt, plain, regular fat',
    shortName: 'Plain yoghurt',
    per100g: { energyKcal: 61, proteinG: 3.5, carbsG: 4.7, fibreG: 0, calciumMg: 121, ironMg: 0.05, waterG: 88 },
  },
  {
    id: 'f-9',
    name: 'Apple, raw, with skin',
    shortName: 'Apple',
    per100g: { energyKcal: 52, proteinG: 0.3, carbsG: 13.8, fibreG: 2.4, calciumMg: 6, ironMg: 0.1, waterG: 86 },
  },
  {
    id: 'f-10',
    name: 'Peanut butter, smooth, vitamin and mineral fortified',
    shortName: 'Peanut butter',
    per100g: { energyKcal: 598, proteinG: 22.5, carbsG: 22.3, fibreG: 5.2, calciumMg: 49, ironMg: 1.9, waterG: 1.8 },
  },
  {
    id: 'f-11',
    name: 'Avocado, raw, flesh only',
    shortName: 'Avocado',
    per100g: { energyKcal: 160, proteinG: 2, carbsG: 8.5, fibreG: 6.7, calciumMg: 12, ironMg: 0.55, waterG: 73, magnesiumMg: 29, sodiumMg: 7 },
  },
  // --- Extended Australian foods (AUSNUT 2011-13 per-100g values) ---
  {
    id: 'f-12',
    name: 'Rice, brown, medium-grain, cooked',
    shortName: 'Brown rice, cooked',
    per100g: { energyKcal: 111, proteinG: 2.6, carbsG: 22.8, fibreG: 1.8, calciumMg: 10, ironMg: 0.5, waterG: 73, magnesiumMg: 44, sodiumMg: 5, zincMg: 0.6 },
  },
  {
    id: 'f-13',
    name: 'Pasta, wholemeal, cooked',
    shortName: 'Wholemeal pasta, cooked',
    per100g: { energyKcal: 149, proteinG: 5.7, carbsG: 29.2, fibreG: 3.9, calciumMg: 15, ironMg: 1.4, waterG: 63, magnesiumMg: 28, sodiumMg: 3 },
  },
  {
    id: 'f-14',
    name: 'Rice cakes, plain, puffed',
    shortName: 'Rice cakes',
    per100g: { energyKcal: 385, proteinG: 7.5, carbsG: 80.9, fibreG: 1.8, calciumMg: 6, ironMg: 0.9, waterG: 8, sodiumMg: 26 },
  },
  {
    id: 'f-15',
    name: 'Tortilla, wheat flour, medium',
    shortName: 'Wheat wrap',
    per100g: { energyKcal: 293, proteinG: 7.7, carbsG: 52.8, fibreG: 3.2, calciumMg: 117, ironMg: 2.4, waterG: 32, sodiumMg: 536 },
  },
  {
    id: 'f-16',
    name: 'Muesli, toasted, with fruit and nuts',
    shortName: 'Granola / toasted muesli',
    per100g: { energyKcal: 436, proteinG: 9.1, carbsG: 62.3, fibreG: 6.5, calciumMg: 45, ironMg: 2.8, waterG: 5, magnesiumMg: 60, sodiumMg: 95, zincMg: 2.0 },
  },
  {
    id: 'f-17',
    name: 'Honey, commercial',
    shortName: 'Honey',
    per100g: { energyKcal: 304, proteinG: 0.3, carbsG: 82.4, fibreG: 0.2, calciumMg: 6, ironMg: 0.4, waterG: 17, sodiumMg: 4 },
  },
  {
    id: 'f-18',
    name: 'Blueberries, raw',
    shortName: 'Blueberries',
    per100g: { energyKcal: 57, proteinG: 0.7, carbsG: 14.5, fibreG: 2.4, calciumMg: 6, ironMg: 0.3, waterG: 84, magnesiumMg: 6, sodiumMg: 1 },
  },
  {
    id: 'f-19',
    name: 'Strawberries, raw',
    shortName: 'Strawberries',
    per100g: { energyKcal: 32, proteinG: 0.7, carbsG: 7.7, fibreG: 2.0, calciumMg: 16, ironMg: 0.4, waterG: 91, magnesiumMg: 13, sodiumMg: 1 },
  },
  {
    id: 'f-20',
    name: 'Tomato, cherry, raw',
    shortName: 'Cherry tomatoes',
    per100g: { energyKcal: 18, proteinG: 0.9, carbsG: 3.9, fibreG: 1.2, calciumMg: 10, ironMg: 0.4, waterG: 94, magnesiumMg: 11, sodiumMg: 5 },
  },
  {
    id: 'f-21',
    name: 'Broccoli, raw',
    shortName: 'Broccoli',
    per100g: { energyKcal: 34, proteinG: 2.8, carbsG: 6.6, fibreG: 2.6, calciumMg: 47, ironMg: 0.7, waterG: 89, magnesiumMg: 21, sodiumMg: 33, zincMg: 0.4 },
  },
  {
    id: 'f-22',
    name: 'Carrot, raw',
    shortName: 'Carrot',
    per100g: { energyKcal: 41, proteinG: 0.9, carbsG: 9.6, fibreG: 2.8, calciumMg: 33, ironMg: 0.3, waterG: 88, magnesiumMg: 12, sodiumMg: 69 },
  },
  {
    id: 'f-23',
    name: 'Capsicum, red, raw',
    shortName: 'Red capsicum',
    per100g: { energyKcal: 31, proteinG: 1.0, carbsG: 7.2, fibreG: 2.1, calciumMg: 7, ironMg: 0.4, waterG: 92, magnesiumMg: 12, sodiumMg: 4 },
  },
  {
    id: 'f-24',
    name: 'Chickpeas, canned, drained',
    shortName: 'Chickpeas, canned',
    per100g: { energyKcal: 120, proteinG: 7.2, carbsG: 16.9, fibreG: 5.4, calciumMg: 49, ironMg: 1.8, waterG: 70, magnesiumMg: 40, sodiumMg: 240, zincMg: 1.1 },
  },
  {
    id: 'f-25',
    name: 'Lentils, red, cooked',
    shortName: 'Red lentils, cooked',
    per100g: { energyKcal: 116, proteinG: 9.0, carbsG: 20.1, fibreG: 3.7, calciumMg: 19, ironMg: 3.3, waterG: 70, magnesiumMg: 36, sodiumMg: 2, zincMg: 1.3 },
  },
  {
    id: 'f-26',
    name: 'Kidney beans, canned, drained',
    shortName: 'Kidney beans, canned',
    per100g: { energyKcal: 103, proteinG: 6.9, carbsG: 16.8, fibreG: 5.4, calciumMg: 38, ironMg: 2.0, waterG: 73, magnesiumMg: 33, sodiumMg: 230, zincMg: 0.9 },
  },
  {
    id: 'f-27',
    name: 'Tuna, canned in spring water, drained',
    shortName: 'Tuna, canned',
    per100g: { energyKcal: 109, proteinG: 25.5, carbsG: 0, fibreG: 0, calciumMg: 12, ironMg: 0.9, waterG: 73, sodiumMg: 290, zincMg: 0.7, omega3G: 0.26, vitaminDUg: 4.0 },
  },
  {
    id: 'f-28',
    name: 'Salmon, Atlantic, grilled',
    shortName: 'Salmon, grilled',
    per100g: { energyKcal: 206, proteinG: 28.8, carbsG: 0, fibreG: 0, calciumMg: 12, ironMg: 0.6, waterG: 64, sodiumMg: 68, zincMg: 0.8, omega3G: 2.26, vitaminDUg: 11.0 },
  },
  {
    id: 'f-29',
    name: 'Beef mince, lean (< 10% fat), cooked',
    shortName: 'Beef mince, lean',
    per100g: { energyKcal: 195, proteinG: 28.0, carbsG: 0, fibreG: 0, calciumMg: 14, ironMg: 2.7, waterG: 61, magnesiumMg: 24, sodiumMg: 70, zincMg: 5.3 },
  },
  {
    id: 'f-30',
    name: 'Turkey breast, lean, cooked',
    shortName: 'Turkey breast',
    per100g: { energyKcal: 135, proteinG: 30.0, carbsG: 0, fibreG: 0, calciumMg: 15, ironMg: 1.4, waterG: 68, sodiumMg: 60, zincMg: 2.2 },
  },
  {
    id: 'f-31',
    name: 'Tofu, firm, raw',
    shortName: 'Firm tofu',
    per100g: { energyKcal: 76, proteinG: 8.1, carbsG: 1.9, fibreG: 0.3, calciumMg: 350, ironMg: 1.6, waterG: 84, magnesiumMg: 30, sodiumMg: 7, zincMg: 0.8 },
  },
  {
    id: 'f-32',
    name: 'Cheese, cheddar, full fat',
    shortName: 'Cheddar cheese',
    per100g: { energyKcal: 403, proteinG: 25.4, carbsG: 0.1, fibreG: 0, calciumMg: 721, ironMg: 0.5, waterG: 36, sodiumMg: 621, zincMg: 3.1 },
  },
  {
    id: 'f-33',
    name: 'Cheese, parmesan, grated',
    shortName: 'Parmesan',
    per100g: { energyKcal: 392, proteinG: 35.7, carbsG: 3.2, fibreG: 0, calciumMg: 1184, ironMg: 0.5, waterG: 29, sodiumMg: 1529, zincMg: 3.0 },
  },
  {
    id: 'f-34',
    name: 'Milk, cow, reduced fat (2%), pasteurised',
    shortName: 'Reduced fat milk',
    per100g: { energyKcal: 50, proteinG: 3.4, carbsG: 5.0, fibreG: 0, calciumMg: 122, ironMg: 0.03, waterG: 89, sodiumMg: 48, zincMg: 0.4 },
  },
  {
    id: 'f-35',
    name: 'Milk, flavoured, chocolate, commercial',
    shortName: 'Chocolate milk',
    per100g: { energyKcal: 68, proteinG: 3.4, carbsG: 11.4, fibreG: 0.3, calciumMg: 115, ironMg: 0.4, waterG: 83, sodiumMg: 52 },
  },
  {
    id: 'f-36',
    name: 'Yoghurt, Greek-style, full fat, plain',
    shortName: 'Greek yoghurt, full fat',
    per100g: { energyKcal: 97, proteinG: 9.0, carbsG: 3.6, fibreG: 0, calciumMg: 110, ironMg: 0.1, waterG: 82, sodiumMg: 47, zincMg: 0.5 },
  },
  {
    id: 'f-37',
    name: 'Milk, soy, regular, calcium-fortified',
    shortName: 'Soy milk',
    per100g: { energyKcal: 41, proteinG: 3.3, carbsG: 2.8, fibreG: 0.5, calciumMg: 120, ironMg: 0.5, waterG: 91, sodiumMg: 51, vitaminDUg: 1.0 },
  },
  {
    id: 'f-38',
    name: 'Milk, oat, regular, calcium-fortified',
    shortName: 'Oat milk',
    per100g: { energyKcal: 45, proteinG: 1.0, carbsG: 7.8, fibreG: 0.8, calciumMg: 120, ironMg: 0.3, waterG: 90, sodiumMg: 52, vitaminDUg: 1.0 },
  },
  {
    id: 'f-39',
    name: 'Hummus, commercial',
    shortName: 'Hummus',
    per100g: { energyKcal: 177, proteinG: 7.9, carbsG: 11.6, fibreG: 6.0, calciumMg: 53, ironMg: 2.2, waterG: 63, magnesiumMg: 37, sodiumMg: 430, zincMg: 1.1 },
  },
  {
    id: 'f-40',
    name: 'Tahini (sesame seed paste)',
    shortName: 'Tahini',
    per100g: { energyKcal: 595, proteinG: 17.0, carbsG: 21.2, fibreG: 9.3, calciumMg: 426, ironMg: 8.9, waterG: 4, magnesiumMg: 95, sodiumMg: 115, zincMg: 4.6 },
  },
  {
    id: 'f-41',
    name: 'Juice, orange, unsweetened',
    shortName: 'Orange juice',
    per100g: { energyKcal: 45, proteinG: 0.7, carbsG: 10.4, fibreG: 0.2, calciumMg: 11, ironMg: 0.2, waterG: 88, magnesiumMg: 11, sodiumMg: 1 },
  },
  {
    id: 'f-42',
    name: 'Sports drink, isotonic, commercial (e.g. Gatorade)',
    shortName: 'Sports drink',
    per100g: { energyKcal: 26, proteinG: 0, carbsG: 6.5, fibreG: 0, calciumMg: 3, ironMg: 0, waterG: 93, sodiumMg: 45 },
  },
  {
    id: 'f-43',
    name: 'Water, plain',
    shortName: 'Water',
    per100g: { energyKcal: 0, proteinG: 0, carbsG: 0, fibreG: 0, calciumMg: 0, ironMg: 0, waterG: 100, sodiumMg: 0 },
  },
  {
    id: 'f-44',
    name: 'Sultanas, dried',
    shortName: 'Sultanas',
    per100g: { energyKcal: 299, proteinG: 2.5, carbsG: 74.8, fibreG: 4.5, calciumMg: 24, ironMg: 1.8, waterG: 16, magnesiumMg: 31, sodiumMg: 26, zincMg: 0.2 },
  },
  {
    id: 'f-45',
    name: 'Dates, Medjool, dried',
    shortName: 'Medjool dates',
    per100g: { energyKcal: 277, proteinG: 1.8, carbsG: 74.9, fibreG: 6.7, calciumMg: 64, ironMg: 0.9, waterG: 21, magnesiumMg: 54, sodiumMg: 1 },
  },
  {
    id: 'f-46',
    name: 'Quinoa, cooked',
    shortName: 'Quinoa, cooked',
    per100g: { energyKcal: 120, proteinG: 4.4, carbsG: 21.3, fibreG: 2.8, calciumMg: 17, ironMg: 1.5, waterG: 72, magnesiumMg: 64, sodiumMg: 7, zincMg: 1.1 },
  },
  {
    id: 'f-47',
    name: 'Crackers, wholegrain, plain (e.g. Vita-Weat)',
    shortName: 'Wholegrain crackers',
    per100g: { energyKcal: 381, proteinG: 10.4, carbsG: 68.0, fibreG: 9.8, calciumMg: 44, ironMg: 2.8, waterG: 5, sodiumMg: 490 },
  },
  {
    id: 'f-48',
    name: 'Mango, raw, flesh only',
    shortName: 'Mango',
    per100g: { energyKcal: 60, proteinG: 0.8, carbsG: 15.0, fibreG: 1.6, calciumMg: 11, ironMg: 0.2, waterG: 83, magnesiumMg: 10, sodiumMg: 2 },
  },
  {
    id: 'f-49',
    name: 'Orange, raw, flesh only',
    shortName: 'Orange',
    per100g: { energyKcal: 47, proteinG: 0.9, carbsG: 11.8, fibreG: 2.4, calciumMg: 40, ironMg: 0.1, waterG: 87, magnesiumMg: 10, sodiumMg: 0 },
  },
  {
    id: 'f-50',
    name: 'Sweet potato, orange-fleshed, baked',
    shortName: 'Sweet potato, baked',
    per100g: { energyKcal: 90, proteinG: 2.0, carbsG: 20.7, fibreG: 3.3, calciumMg: 38, ironMg: 0.6, waterG: 75, magnesiumMg: 25, sodiumMg: 36 },
  },
  {
    id: 'f-51',
    name: 'Spinach, raw',
    shortName: 'Spinach',
    per100g: { energyKcal: 23, proteinG: 2.9, carbsG: 3.6, fibreG: 2.2, calciumMg: 99, ironMg: 2.7, waterG: 92, magnesiumMg: 79, sodiumMg: 79 },
  },
  {
    id: 'f-52',
    name: 'Corn, sweet, kernel, cooked',
    shortName: 'Sweet corn',
    per100g: { energyKcal: 86, proteinG: 3.2, carbsG: 19.0, fibreG: 2.7, calciumMg: 3, ironMg: 0.5, waterG: 76, magnesiumMg: 26, sodiumMg: 1 },
  },
  {
    id: 'f-53',
    name: 'Almond butter, smooth',
    shortName: 'Almond butter',
    per100g: { energyKcal: 614, proteinG: 20.9, carbsG: 18.8, fibreG: 10.5, calciumMg: 347, ironMg: 3.7, waterG: 2, magnesiumMg: 270, sodiumMg: 7, zincMg: 3.1 },
  },
  {
    id: 'f-54',
    name: 'Coconut water, plain',
    shortName: 'Coconut water',
    per100g: { energyKcal: 19, proteinG: 0.7, carbsG: 3.7, fibreG: 1.1, calciumMg: 24, ironMg: 0.3, waterG: 95, magnesiumMg: 25, sodiumMg: 105 },
  },
  {
    id: 'f-55',
    name: 'Juice, apple, unsweetened',
    shortName: 'Apple juice',
    per100g: { energyKcal: 46, proteinG: 0.1, carbsG: 11.4, fibreG: 0.2, calciumMg: 8, ironMg: 0.2, waterG: 88, sodiumMg: 4 },
  },
  // --- Australian staple cereals ---
  {
    id: 'f-56',
    name: 'Weet-Bix, plain (Sanitarium)',
    shortName: 'Weet-Bix',
    per100g: { energyKcal: 349, proteinG: 11.9, carbsG: 67.5, fibreG: 10.5, calciumMg: 20, ironMg: 7.0, waterG: 4, magnesiumMg: 90, sodiumMg: 270, zincMg: 3.5, vitaminDUg: 2.5 },
  },
  {
    id: 'f-57',
    name: 'Bread, white, sliced, commercial',
    shortName: 'White bread',
    per100g: { energyKcal: 237, proteinG: 8.5, carbsG: 47.5, fibreG: 2.7, calciumMg: 142, ironMg: 1.6, waterG: 35, sodiumMg: 490 },
  },
  {
    id: 'f-58',
    name: 'Oats, rolled, cooked as porridge with water',
    shortName: 'Porridge (cooked oats)',
    per100g: { energyKcal: 71, proteinG: 2.5, carbsG: 12.1, fibreG: 1.7, calciumMg: 9, ironMg: 0.7, waterG: 84, magnesiumMg: 12, sodiumMg: 2 },
  },
  // --- More proteins ---
  {
    id: 'f-59',
    name: 'Sardines, canned in oil, drained',
    shortName: 'Sardines, canned',
    per100g: { energyKcal: 208, proteinG: 24.6, carbsG: 0, fibreG: 0, calciumMg: 382, ironMg: 2.9, waterG: 57, sodiumMg: 400, zincMg: 1.3, omega3G: 3.0, vitaminDUg: 4.8 },
  },
  {
    id: 'f-60',
    name: 'Salmon, canned in water, drained',
    shortName: 'Salmon, canned',
    per100g: { energyKcal: 153, proteinG: 25.0, carbsG: 0, fibreG: 0, calciumMg: 232, ironMg: 0.9, waterG: 70, sodiumMg: 400, zincMg: 0.7, omega3G: 1.4, vitaminDUg: 11.0 },
  },
  {
    id: 'f-61',
    name: 'Beef, steak, lean, grilled',
    shortName: 'Beef steak, lean',
    per100g: { energyKcal: 212, proteinG: 31.0, carbsG: 0, fibreG: 0, calciumMg: 8, ironMg: 3.0, waterG: 62, magnesiumMg: 26, sodiumMg: 64, zincMg: 6.5 },
  },
  {
    id: 'f-62',
    name: 'Cheese, cottage, low fat',
    shortName: 'Cottage cheese',
    per100g: { energyKcal: 77, proteinG: 12.4, carbsG: 3.4, fibreG: 0, calciumMg: 61, ironMg: 0.1, waterG: 80, sodiumMg: 310, zincMg: 0.4 },
  },
  {
    id: 'f-63',
    name: 'Edamame, boiled',
    shortName: 'Edamame',
    per100g: { energyKcal: 122, proteinG: 11.0, carbsG: 8.9, fibreG: 5.2, calciumMg: 63, ironMg: 2.3, waterG: 68, magnesiumMg: 64, sodiumMg: 4, zincMg: 1.4 },
  },
  {
    id: 'f-64',
    name: 'Beans, black, canned, drained',
    shortName: 'Black beans, canned',
    per100g: { energyKcal: 91, proteinG: 5.3, carbsG: 14.0, fibreG: 5.7, calciumMg: 26, ironMg: 1.5, waterG: 74, magnesiumMg: 27, sodiumMg: 5, zincMg: 0.8 },
  },
  // --- More vegetables ---
  {
    id: 'f-65',
    name: 'Cucumber, raw',
    shortName: 'Cucumber',
    per100g: { energyKcal: 16, proteinG: 0.7, carbsG: 3.6, fibreG: 0.5, calciumMg: 16, ironMg: 0.3, waterG: 95, sodiumMg: 2 },
  },
  {
    id: 'f-66',
    name: 'Lettuce, mixed salad greens, raw',
    shortName: 'Salad greens',
    per100g: { energyKcal: 15, proteinG: 1.4, carbsG: 2.8, fibreG: 2.0, calciumMg: 36, ironMg: 0.9, waterG: 95, sodiumMg: 10 },
  },
  {
    id: 'f-67',
    name: 'Mushrooms, button, raw',
    shortName: 'Mushrooms',
    per100g: { energyKcal: 22, proteinG: 3.1, carbsG: 3.3, fibreG: 1.0, calciumMg: 6, ironMg: 0.5, waterG: 93, sodiumMg: 5, zincMg: 0.7 },
  },
  {
    id: 'f-68',
    name: 'Peas, green, frozen, cooked',
    shortName: 'Peas',
    per100g: { energyKcal: 80, proteinG: 5.4, carbsG: 14.4, fibreG: 5.1, calciumMg: 22, ironMg: 1.5, waterG: 79, magnesiumMg: 25, sodiumMg: 90, zincMg: 0.7 },
  },
  {
    id: 'f-69',
    name: 'Zucchini (courgette), raw',
    shortName: 'Zucchini',
    per100g: { energyKcal: 17, proteinG: 1.2, carbsG: 3.1, fibreG: 1.0, calciumMg: 16, ironMg: 0.4, waterG: 94, sodiumMg: 8 },
  },
  // --- More fruit ---
  {
    id: 'f-70',
    name: 'Grapes, raw',
    shortName: 'Grapes',
    per100g: { energyKcal: 69, proteinG: 0.7, carbsG: 18.1, fibreG: 0.9, calciumMg: 10, ironMg: 0.4, waterG: 81, sodiumMg: 2 },
  },
  {
    id: 'f-71',
    name: 'Watermelon, raw, flesh only',
    shortName: 'Watermelon',
    per100g: { energyKcal: 30, proteinG: 0.6, carbsG: 7.5, fibreG: 0.4, calciumMg: 7, ironMg: 0.2, waterG: 91, magnesiumMg: 10, sodiumMg: 1 },
  },
  {
    id: 'f-72',
    name: 'Kiwifruit, raw, flesh only',
    shortName: 'Kiwifruit',
    per100g: { energyKcal: 61, proteinG: 1.1, carbsG: 14.7, fibreG: 3.0, calciumMg: 34, ironMg: 0.3, waterG: 83, magnesiumMg: 17, sodiumMg: 3 },
  },
  {
    id: 'f-73',
    name: 'Mandarin, raw, flesh only',
    shortName: 'Mandarin',
    per100g: { energyKcal: 53, proteinG: 0.8, carbsG: 13.3, fibreG: 1.8, calciumMg: 37, ironMg: 0.2, waterG: 85, sodiumMg: 2 },
  },
  // --- Australian staple drink ---
  {
    id: 'f-74',
    name: 'Milo, chocolate malt powder (Nestlé)',
    shortName: 'Milo powder',
    per100g: { energyKcal: 374, proteinG: 9.4, carbsG: 71.0, fibreG: 1.5, calciumMg: 500, ironMg: 12.0, waterG: 2, sodiumMg: 200, zincMg: 3.3, vitaminDUg: 5.0 },
  },
  // --- Snack ---
  {
    id: 'f-75',
    name: 'Muesli bar, commercial, fruit and oat',
    shortName: 'Muesli bar',
    per100g: { energyKcal: 398, proteinG: 6.0, carbsG: 72.0, fibreG: 3.5, calciumMg: 51, ironMg: 2.5, waterG: 9, sodiumMg: 170 },
  },
  {
    id: 'f-76',
    name: 'Gyoza, vegetarian, steamed or boiled',
    shortName: 'Vegetarian gyoza',
    per100g: { energyKcal: 176, proteinG: 5.5, fatG: 1.4, carbsG: 33.2, fibreG: 0, calciumMg: 0, ironMg: 0, waterG: 60, sodiumMg: 378 },
  },
  // --- Priority lookup items ---
  {
    id: 'f-77',
    name: 'Potato, white, baked in skin (jacket potato)',
    shortName: 'Jacket potato',
    per100g: { energyKcal: 93, proteinG: 2.5, carbsG: 21.1, fibreG: 2.2, calciumMg: 12, ironMg: 1.1, waterG: 75, magnesiumMg: 28, sodiumMg: 10, zincMg: 0.4 },
  },
  {
    id: 'f-78',
    name: 'Baked beans, canned in tomato sauce',
    shortName: 'Baked beans',
    per100g: { energyKcal: 84, proteinG: 4.8, carbsG: 14.5, fibreG: 3.7, calciumMg: 45, ironMg: 1.4, waterG: 73, magnesiumMg: 27, sodiumMg: 430, zincMg: 0.5 },
  },
  {
    id: 'f-79',
    name: 'Bagel, plain, commercial',
    shortName: 'Bagel, plain',
    per100g: { energyKcal: 270, proteinG: 10.1, carbsG: 53.2, fibreG: 2.3, calciumMg: 30, ironMg: 3.0, waterG: 33, sodiumMg: 440 },
  },
  {
    id: 'f-80',
    name: 'Pitta bread, white, commercial',
    shortName: 'Pitta bread',
    per100g: { energyKcal: 274, proteinG: 9.0, carbsG: 55.0, fibreG: 2.3, calciumMg: 58, ironMg: 1.8, waterG: 32, sodiumMg: 500 },
  },
  {
    id: 'f-81',
    name: 'Potato, new, boiled',
    shortName: 'New potatoes, boiled',
    per100g: { energyKcal: 70, proteinG: 1.7, carbsG: 15.4, fibreG: 1.4, calciumMg: 5, ironMg: 0.3, waterG: 80, magnesiumMg: 18, sodiumMg: 5 },
  },
  // --- Bread and grain staples ---
  {
    id: 'f-82',
    name: 'Pasta, white, cooked',
    shortName: 'White pasta, cooked',
    per100g: { energyKcal: 158, proteinG: 5.8, carbsG: 31.0, fibreG: 1.8, calciumMg: 7, ironMg: 0.5, waterG: 62, sodiumMg: 1 },
  },
  {
    id: 'f-83',
    name: 'Bread roll, white, commercial',
    shortName: 'White bread roll',
    per100g: { energyKcal: 266, proteinG: 8.5, carbsG: 51.2, fibreG: 2.6, calciumMg: 130, ironMg: 2.0, waterG: 35, sodiumMg: 500 },
  },
  {
    id: 'f-84',
    name: 'Bread, sourdough, white, commercially prepared',
    shortName: 'Sourdough bread',
    per100g: { energyKcal: 260, proteinG: 9.5, carbsG: 51.0, fibreG: 2.8, calciumMg: 25, ironMg: 1.5, waterG: 36, sodiumMg: 450 },
  },
  {
    id: 'f-85',
    name: 'Bread, multigrain, commercially prepared',
    shortName: 'Multigrain bread',
    per100g: { energyKcal: 241, proteinG: 9.0, carbsG: 43.0, fibreG: 4.5, calciumMg: 85, ironMg: 2.0, waterG: 36, sodiumMg: 460 },
  },
  {
    id: 'f-86',
    name: 'English muffin, white, commercial',
    shortName: 'English muffin',
    per100g: { energyKcal: 222, proteinG: 8.5, carbsG: 43.0, fibreG: 2.0, calciumMg: 95, ironMg: 2.0, waterG: 44, sodiumMg: 390 },
  },
  {
    id: 'f-87',
    name: 'Crumpet, commercial',
    shortName: 'Crumpet',
    per100g: { energyKcal: 194, proteinG: 6.3, carbsG: 38.2, fibreG: 1.8, calciumMg: 60, ironMg: 1.0, waterG: 51, sodiumMg: 500 },
  },
  {
    id: 'f-88',
    name: 'Corn thins, plain, rice and corn cakes',
    shortName: 'Corn thins',
    per100g: { energyKcal: 355, proteinG: 8.0, carbsG: 73.0, fibreG: 4.0, calciumMg: 10, ironMg: 1.2, waterG: 7, sodiumMg: 290 },
  },
  // --- Potato varieties ---
  {
    id: 'f-89',
    name: 'Potato, white, boiled, no skin',
    shortName: 'Potato, boiled',
    per100g: { energyKcal: 77, proteinG: 1.8, carbsG: 17.6, fibreG: 1.6, calciumMg: 8, ironMg: 0.3, waterG: 79, magnesiumMg: 22, sodiumMg: 5 },
  },
  {
    id: 'f-90',
    name: 'Potato, mashed, made with milk and butter',
    shortName: 'Mashed potato',
    per100g: { energyKcal: 113, proteinG: 2.0, carbsG: 16.5, fibreG: 1.5, calciumMg: 20, ironMg: 0.3, waterG: 78, sodiumMg: 295 },
  },
  // --- Proteins ---
  {
    id: 'f-91',
    name: 'Chicken, thigh, skinless, cooked',
    shortName: 'Chicken thigh',
    per100g: { energyKcal: 179, proteinG: 26.0, carbsG: 0, fibreG: 0, calciumMg: 12, ironMg: 1.1, waterG: 65, sodiumMg: 85, zincMg: 2.7 },
  },
  {
    id: 'f-92',
    name: 'Lamb cutlet, lean, grilled',
    shortName: 'Lamb cutlet',
    per100g: { energyKcal: 202, proteinG: 28.5, carbsG: 0, fibreG: 0, calciumMg: 17, ironMg: 2.2, waterG: 61, sodiumMg: 70, zincMg: 4.1 },
  },
  {
    id: 'f-93',
    name: 'Pork, loin, lean, cooked',
    shortName: 'Pork loin',
    per100g: { energyKcal: 185, proteinG: 29.0, carbsG: 0, fibreG: 0, calciumMg: 14, ironMg: 0.7, waterG: 63, sodiumMg: 65, zincMg: 2.9 },
  },
  {
    id: 'f-94',
    name: 'Beef mince, regular fat (~20% fat), cooked',
    shortName: 'Beef mince, regular',
    per100g: { energyKcal: 286, proteinG: 25.0, carbsG: 0, fibreG: 0, calciumMg: 10, ironMg: 2.5, waterG: 52, sodiumMg: 80, zincMg: 5.0 },
  },
  {
    id: 'f-95',
    name: 'Kangaroo, mince, cooked',
    shortName: 'Kangaroo mince',
    per100g: { energyKcal: 147, proteinG: 29.0, carbsG: 0, fibreG: 0, calciumMg: 6, ironMg: 4.0, waterG: 66, sodiumMg: 50, zincMg: 3.5, omega3G: 0.1 },
  },
  {
    id: 'f-96',
    name: 'Prawn, cooked, flesh only',
    shortName: 'Prawns, cooked',
    per100g: { energyKcal: 99, proteinG: 20.9, carbsG: 0.9, fibreG: 0, calciumMg: 52, ironMg: 0.5, waterG: 77, sodiumMg: 185, zincMg: 1.6 },
  },
  {
    id: 'f-97',
    name: 'Ham, leg, sliced, lean',
    shortName: 'Ham, sliced',
    per100g: { energyKcal: 107, proteinG: 17.2, carbsG: 1.0, fibreG: 0, calciumMg: 6, ironMg: 0.7, waterG: 74, sodiumMg: 900, zincMg: 1.5 },
  },
  {
    id: 'f-98',
    name: 'Bacon, middle rasher, cooked',
    shortName: 'Bacon',
    per100g: { energyKcal: 400, proteinG: 27.0, carbsG: 0.5, fibreG: 0, calciumMg: 9, ironMg: 0.7, waterG: 42, sodiumMg: 1650, zincMg: 2.2 },
  },
  // --- Dairy and eggs ---
  {
    id: 'f-99',
    name: 'Cheese, feta',
    shortName: 'Feta cheese',
    per100g: { energyKcal: 264, proteinG: 14.2, carbsG: 4.1, fibreG: 0, calciumMg: 493, ironMg: 0.7, waterG: 56, sodiumMg: 917, zincMg: 2.9 },
  },
  {
    id: 'f-100',
    name: 'Cheese, ricotta, part skim',
    shortName: 'Ricotta cheese',
    per100g: { energyKcal: 138, proteinG: 11.3, carbsG: 3.0, fibreG: 0, calciumMg: 207, ironMg: 0.4, waterG: 74, sodiumMg: 89, zincMg: 1.4 },
  },
  {
    id: 'f-101',
    name: 'Yoghurt, Greek-style, low fat, plain',
    shortName: 'Greek yoghurt, low fat',
    per100g: { energyKcal: 59, proteinG: 10.0, carbsG: 3.2, fibreG: 0, calciumMg: 130, ironMg: 0.1, waterG: 85, sodiumMg: 50, zincMg: 0.5 },
  },
  {
    id: 'f-102',
    name: 'Egg, chicken, whole, hard boiled',
    shortName: 'Egg, hard boiled',
    per100g: { energyKcal: 155, proteinG: 12.6, carbsG: 1.1, fibreG: 0, calciumMg: 50, ironMg: 1.9, waterG: 74, sodiumMg: 124, vitaminDUg: 1.8 },
  },
  // --- Nuts and seeds ---
  {
    id: 'f-103',
    name: 'Almonds, raw',
    shortName: 'Almonds',
    per100g: { energyKcal: 579, proteinG: 21.2, carbsG: 21.7, fibreG: 12.5, calciumMg: 264, ironMg: 3.7, waterG: 4, magnesiumMg: 270, sodiumMg: 1, zincMg: 3.1 },
  },
  {
    id: 'f-104',
    name: 'Walnuts, raw',
    shortName: 'Walnuts',
    per100g: { energyKcal: 654, proteinG: 15.2, carbsG: 13.7, fibreG: 6.7, calciumMg: 98, ironMg: 2.9, waterG: 4, magnesiumMg: 158, sodiumMg: 2, omega3G: 9.1 },
  },
  {
    id: 'f-105',
    name: 'Cashews, raw, unsalted',
    shortName: 'Cashews',
    per100g: { energyKcal: 553, proteinG: 18.2, carbsG: 30.2, fibreG: 3.3, calciumMg: 37, ironMg: 6.7, waterG: 5, magnesiumMg: 292, sodiumMg: 12, zincMg: 5.6 },
  },
  {
    id: 'f-106',
    name: 'Mixed nuts, unsalted (e.g. almonds, cashews, walnuts)',
    shortName: 'Mixed nuts',
    per100g: { energyKcal: 607, proteinG: 20.0, carbsG: 13.2, fibreG: 6.0, calciumMg: 115, ironMg: 3.0, waterG: 2, magnesiumMg: 163, sodiumMg: 5, zincMg: 3.2 },
  },
  // --- Sports and performance foods ---
  {
    id: 'f-107',
    name: 'Protein powder, whey, vanilla flavour',
    shortName: 'Whey protein powder',
    per100g: { energyKcal: 390, proteinG: 80.0, carbsG: 8.0, fibreG: 0, calciumMg: 600, ironMg: 0.5, waterG: 4, sodiumMg: 200, zincMg: 2.0 },
  },
  {
    id: 'f-108',
    name: 'Energy gel, commercial (e.g. GU, Maurten)',
    shortName: 'Energy gel',
    per100g: { energyKcal: 250, proteinG: 0.5, carbsG: 60.0, fibreG: 0, calciumMg: 0, ironMg: 0, waterG: 35, sodiumMg: 125 },
  },
  {
    id: 'f-109',
    name: 'Sports bar, commercial (e.g. Clif, Trek)',
    shortName: 'Sports bar',
    per100g: { energyKcal: 380, proteinG: 10.0, carbsG: 65.0, fibreG: 5.0, calciumMg: 60, ironMg: 2.5, waterG: 8, sodiumMg: 150 },
  },
  // --- Condiments and cooking basics ---
  {
    id: 'f-110',
    name: 'Vegemite (Bega Cheese)',
    shortName: 'Vegemite',
    per100g: { energyKcal: 155, proteinG: 27.4, carbsG: 18.7, fibreG: 4.6, calciumMg: 73, ironMg: 6.0, waterG: 28, sodiumMg: 3400 },
  },
  {
    id: 'f-111',
    name: 'Olive oil',
    shortName: 'Olive oil',
    per100g: { energyKcal: 884, proteinG: 0, carbsG: 0, fibreG: 0, calciumMg: 1, ironMg: 0.6, waterG: 0, sodiumMg: 2 },
  },
  {
    id: 'f-112',
    name: 'Butter, unsalted',
    shortName: 'Butter',
    per100g: { energyKcal: 717, proteinG: 0.9, carbsG: 0.1, fibreG: 0, calciumMg: 24, ironMg: 0.02, waterG: 16, sodiumMg: 11 },
  },
  // --- Vegetables ---
  {
    id: 'f-113',
    name: 'Tomato, whole, raw',
    shortName: 'Tomato',
    per100g: { energyKcal: 18, proteinG: 0.9, carbsG: 3.9, fibreG: 1.2, calciumMg: 10, ironMg: 0.4, waterG: 94, magnesiumMg: 11, sodiumMg: 5 },
  },
  {
    id: 'f-114',
    name: 'Onion, brown, raw',
    shortName: 'Onion',
    per100g: { energyKcal: 40, proteinG: 1.1, carbsG: 9.3, fibreG: 1.7, calciumMg: 23, ironMg: 0.2, waterG: 89, sodiumMg: 4 },
  },
  {
    id: 'f-115',
    name: 'Corn, sweet, canned, drained',
    shortName: 'Corn, canned',
    per100g: { energyKcal: 83, proteinG: 2.5, carbsG: 18.1, fibreG: 2.4, calciumMg: 4, ironMg: 0.5, waterG: 77, sodiumMg: 270 },
  },
  // --- Snacks and baked goods ---
  {
    id: 'f-116',
    name: 'Banana bread, commercial',
    shortName: 'Banana bread',
    per100g: { energyKcal: 300, proteinG: 4.5, carbsG: 52.0, fibreG: 1.8, calciumMg: 36, ironMg: 1.0, waterG: 28, sodiumMg: 250 },
  },
  // --- Specialty baking and health foods ---
  {
    id: 'f-117',
    name: 'Almond flour',
    shortName: 'Almond flour',
    per100g: { energyKcal: 571, proteinG: 21.4, carbsG: 21.4, fibreG: 10.7, calciumMg: 264, ironMg: 3.7, waterG: 4.4 },
  },
  {
    id: 'f-131',
    name: 'Almond meal',
    shortName: 'Almond meal',
    per100g: { energyKcal: 601, proteinG: 21.9, carbsG: 10.4, fibreG: 10.9, calciumMg: 269, ironMg: 3.9, waterG: 4.2 },
  },
  {
    id: 'f-118',
    name: 'Flaxseeds, whole, raw',
    shortName: 'Flax seeds',
    per100g: { energyKcal: 534, proteinG: 18.3, carbsG: 28.9, fibreG: 27.3, calciumMg: 255, ironMg: 5.7, waterG: 6.9 },
  },
  {
    id: 'f-119',
    name: 'Chocolate chips, dark',
    shortName: 'Chocolate chips',
    per100g: { energyKcal: 489, proteinG: 5.5, carbsG: 63.1, fibreG: 7.0, calciumMg: 32, ironMg: 5.3, waterG: 1.3 },
  },
  {
    id: 'f-120',
    name: 'Cranberries, dried, sweetened',
    shortName: 'Dried cranberries',
    per100g: { energyKcal: 308, proteinG: 0.2, carbsG: 82.4, fibreG: 5.3, calciumMg: 9, ironMg: 0.3, waterG: 16.0 },
  },
  {
    id: 'f-121',
    name: 'Sunflower seeds, raw',
    shortName: 'Sunflower seeds',
    per100g: { energyKcal: 584, proteinG: 20.8, carbsG: 20.0, fibreG: 8.6, calciumMg: 78, ironMg: 5.3, waterG: 4.7 },
  },
  {
    id: 'f-122',
    name: 'Chia seeds, raw',
    shortName: 'Chia seeds',
    per100g: { energyKcal: 486, proteinG: 16.5, carbsG: 42.1, fibreG: 34.4, calciumMg: 631, ironMg: 7.7, waterG: 5.8 },
  },
  {
    id: 'f-123',
    name: 'Coconut flour',
    shortName: 'Coconut flour',
    per100g: { energyKcal: 400, proteinG: 19.3, carbsG: 57.8, fibreG: 38.5, calciumMg: 26, ironMg: 7.7, waterG: 7.0 },
  },
  {
    id: 'f-124',
    name: 'Maple syrup, pure',
    shortName: 'Maple syrup',
    per100g: { energyKcal: 260, proteinG: 0, carbsG: 67.0, fibreG: 0, calciumMg: 102, ironMg: 0.1, waterG: 32.4 },
  },
  {
    id: 'f-125',
    name: 'Pumpkin seeds, raw',
    shortName: 'Pumpkin seeds',
    per100g: { energyKcal: 559, proteinG: 30.2, carbsG: 10.7, fibreG: 6.0, calciumMg: 46, ironMg: 8.8, waterG: 5.2 },
  },
  {
    id: 'f-126',
    name: 'Coconut oil',
    shortName: 'Coconut oil',
    per100g: { energyKcal: 862, proteinG: 0, carbsG: 0, fibreG: 0, calciumMg: 1, ironMg: 0.05, waterG: 0 },
  },
  {
    id: 'f-127',
    name: 'Tahini, sesame seed paste',
    shortName: 'Tahini',
    per100g: { energyKcal: 595, proteinG: 17.0, carbsG: 21.2, fibreG: 9.3, calciumMg: 426, ironMg: 8.9, waterG: 3.0 },
  },
  {
    id: 'f-128',
    name: 'Cacao powder, raw',
    shortName: 'Cacao powder',
    per100g: { energyKcal: 229, proteinG: 19.6, carbsG: 57.9, fibreG: 33.2, calciumMg: 128, ironMg: 13.9, waterG: 3.0 },
  },
  {
    id: 'f-129',
    name: 'Protein powder, whey, vanilla',
    shortName: 'Whey protein powder',
    per100g: { energyKcal: 370, proteinG: 75.0, carbsG: 10.0, fibreG: 0, calciumMg: 600, ironMg: 1.5, waterG: 5.0 },
  },
  {
    id: 'f-130',
    name: 'Dates, medjool, raw',
    shortName: 'Dates',
    per100g: { energyKcal: 277, proteinG: 1.8, carbsG: 75.0, fibreG: 6.7, calciumMg: 64, ironMg: 0.9, waterG: 21.3 },
  },
  {
    id: 'f-132',
    name: 'Milk, cow, skim (fat-free), pasteurised',
    shortName: 'Skim milk',
    per100g: { energyKcal: 35, proteinG: 3.5, carbsG: 5.0, fibreG: 0, calciumMg: 125, ironMg: 0.03, waterG: 91, sodiumMg: 46, zincMg: 0.4, vitaminDUg: 0.0 },
  },
  {
    id: 'f-133',
    name: 'Milk, cow, lactose-free, full fat',
    shortName: 'Lactose-free milk',
    per100g: { energyKcal: 63, proteinG: 3.2, carbsG: 4.8, fibreG: 0, calciumMg: 120, ironMg: 0.03, waterG: 88, sodiumMg: 44, zincMg: 0.4 },
  },
  {
    id: 'f-134',
    name: 'Milk, almond, unsweetened, calcium-fortified',
    shortName: 'Almond milk (unsweetened)',
    per100g: { energyKcal: 13, proteinG: 0.4, carbsG: 0.5, fibreG: 0.2, calciumMg: 120, ironMg: 0.3, waterG: 96, sodiumMg: 58, vitaminDUg: 1.0 },
  },
  {
    id: 'f-135',
    name: 'Milk, almond, sweetened, calcium-fortified',
    shortName: 'Almond milk (sweetened)',
    per100g: { energyKcal: 24, proteinG: 0.4, carbsG: 3.7, fibreG: 0.2, calciumMg: 120, ironMg: 0.3, waterG: 94, sodiumMg: 58, vitaminDUg: 1.0 },
  },
  {
    id: 'f-136',
    name: 'Milk, coconut, drinking (not canned), calcium-fortified',
    shortName: 'Coconut milk (drink)',
    per100g: { energyKcal: 21, proteinG: 0.1, carbsG: 2.5, fibreG: 0, calciumMg: 120, ironMg: 0.2, waterG: 95, sodiumMg: 42, vitaminDUg: 1.0 },
  },
  {
    id: 'f-137',
    name: 'Milk, rice, regular, calcium-fortified',
    shortName: 'Rice milk',
    per100g: { energyKcal: 47, proteinG: 0.3, carbsG: 9.4, fibreG: 0.2, calciumMg: 120, ironMg: 0.2, waterG: 89, sodiumMg: 42, vitaminDUg: 1.0 },
  },
  {
    id: 'f-138',
    name: 'Milk, macadamia, unsweetened, calcium-fortified',
    shortName: 'Macadamia milk',
    per100g: { energyKcal: 18, proteinG: 0.2, carbsG: 1.0, fibreG: 0.1, calciumMg: 120, ironMg: 0.1, waterG: 96, sodiumMg: 55, vitaminDUg: 1.0 },
  },
  {
    id: 'f-139',
    name: 'Milk, cashew, unsweetened, calcium-fortified',
    shortName: 'Cashew milk',
    per100g: { energyKcal: 16, proteinG: 0.5, carbsG: 1.5, fibreG: 0.1, calciumMg: 120, ironMg: 0.2, waterG: 96, sodiumMg: 60, vitaminDUg: 1.0 },
  },
  {
    id: 'f-140',
    name: 'Milk, hemp, unsweetened, calcium-fortified',
    shortName: 'Hemp milk',
    per100g: { energyKcal: 31, proteinG: 1.0, carbsG: 1.3, fibreG: 0, calciumMg: 120, ironMg: 0.5, waterG: 95, sodiumMg: 65, omega3G: 0.25, vitaminDUg: 1.0 },
  },
  {
    id: 'f-141',
    name: 'Milk, pea protein, unsweetened, calcium-fortified',
    shortName: 'Pea milk',
    per100g: { energyKcal: 30, proteinG: 3.2, carbsG: 0.5, fibreG: 0, calciumMg: 120, ironMg: 0.7, waterG: 95, sodiumMg: 100, vitaminDUg: 1.0 },
  },
]

const MAX_SEARCH = 20

let foodsCache: FSANZFood[] | null = null

/** Load foods (sample for now; later from JSON or API). */
function getFoods(): FSANZFood[] {
  if (foodsCache === null) {
    foodsCache = SAMPLE_FOODS
  }
  return foodsCache
}

/** Set foods from external source (e.g. after loading full AUSNUT JSON). */
export function setFSANZFoods(list: FSANZFood[]): void {
  foodsCache = list.length > 0 ? list : SAMPLE_FOODS
}

/**
 * Load food list from a JSON URL (e.g. /fsanz-foods.json in public/).
 * Expected format: array of { id, name, shortName?, per100g: { energyKcal, proteinG, carbsG, fibreG, calciumMg, ironMg, waterG } }.
 * On failure (404, invalid JSON, or wrong shape) the built-in sample list is kept.
 * For a comprehensive list: use FSANZ Australian Food Composition Database (AFCD) or AUSNUT exports and convert to this shape.
 */
export async function loadFSANZFoodsFromUrl(url: string): Promise<void> {
  try {
    const res = await fetch(url)
    if (!res.ok) return
    const data = await res.json()
    if (!Array.isArray(data)) return
    const list = data as unknown[]
    const valid: FSANZFood[] = []
    for (const item of list) {
      if (
        item &&
        typeof item === 'object' &&
        typeof (item as { id?: unknown }).id === 'string' &&
        typeof (item as { name?: unknown }).name === 'string' &&
        (item as { per100g?: unknown }).per100g &&
        typeof (item as { per100g: unknown }).per100g === 'object'
      ) {
        const p = (item as { per100g: Record<string, unknown> }).per100g
        if (
          typeof p.energyKcal === 'number' &&
          typeof p.proteinG === 'number' &&
          typeof p.carbsG === 'number' &&
          typeof p.fibreG === 'number' &&
          typeof p.calciumMg === 'number' &&
          typeof p.ironMg === 'number' &&
          typeof p.waterG === 'number'
        ) {
          valid.push(item as FSANZFood)
        }
      }
    }
    if (valid.length > 0) setFSANZFoods([...SAMPLE_FOODS, ...valid])
  } catch {
    // Keep sample foods on any error
  }
}

/**
 * Search foods by name (case-insensitive, word/substring match).
 * Returns matches sorted by: exact match first, then by name length.
 */
export function searchFoods(query: string, minScore = 0): FSANZFood[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const list = [...getFoods(), ...getScannedFoods()]
  const words = q.split(/\s+/).filter(Boolean)
  const scored = list
    .map((f) => {
      const nameLower = f.name.toLowerCase()
      const shortLower = f.shortName?.toLowerCase() ?? nameLower
      let score = 0
      if (nameLower === q || shortLower === q) score = 100
      else if (nameLower.startsWith(q) || shortLower.startsWith(q)) score = 80
      else if (nameLower.includes(q) || shortLower.includes(q)) score = 60
      else if (words.every((w) => nameLower.includes(w) || shortLower.includes(w))) score = 40
      else if (words.some((w) => nameLower.includes(w) || shortLower.includes(w))) score = 20
      if (score >= Math.max(minScore, 1)) return { food: f, score }
      return null
    })
    .filter((x): x is { food: FSANZFood; score: number } => x !== null)
    .sort((a, b) => b.score - a.score || a.food.name.length - b.food.name.length)
  return scored.slice(0, MAX_SEARCH).map((x) => x.food)
}

/** Get a single food by id. Checks FSANZ database first, then scanned products cache. */
export function getFoodById(id: string): FSANZFood | null {
  return getFoods().find((f) => f.id === id) ?? getScannedFoods().find((f) => f.id === id) ?? null
}

/**
 * Return nutrient totals for a given food and amount in grams.
 * Uses per-100g data scaled by amountGrams/100.
 */
export function getNutrientsForFood(foodId: string, amountGrams: number): MealNutrients | null {
  const food = getFoodById(foodId)
  if (!food || amountGrams <= 0) return null
  return nutrientsForAmount(food.per100g, amountGrams)
}

/**
 * Return combined nutrient totals for multiple foods (e.g. banana + peanut butter).
 * Sums per-100g data scaled by each amount. Returns null if entries are empty.
 * When an entry has inline `per100g` (e.g. a barcode-scanned product), uses that
 * directly instead of the FSANZ database lookup.
 */
export function getNutrientsForFoods(
  entries: { foodId: string; amountGrams: number; per100g?: NutrientsPer100g }[]
): MealNutrients | null {
  if (entries.length === 0) return null
  let calories = 0
  let protein = 0
  let carbs = 0
  let fibre = 0
  let calcium = 0
  let iron = 0
  let hydration = 0
  let zinc: number | null = null
  let magnesium: number | null = null
  let omega3: number | null = null
  let vitaminD: number | null = null
  let sodium: number | null = null
  for (const fe of entries) {
    const nut = fe.per100g
      ? nutrientsForAmount(fe.per100g, fe.amountGrams)
      : getNutrientsForFood(fe.foodId, fe.amountGrams)
    if (nut) {
      calories += nut.calories
      protein += nut.protein
      carbs += nut.carbs
      fibre += nut.fibre
      calcium += nut.calcium
      iron += nut.iron
      hydration += nut.hydration
      if (nut.zinc != null) zinc = (zinc ?? 0) + nut.zinc
      if (nut.magnesium != null) magnesium = (magnesium ?? 0) + nut.magnesium
      if (nut.omega3 != null) omega3 = (omega3 ?? 0) + nut.omega3
      if (nut.vitaminD != null) vitaminD = (vitaminD ?? 0) + nut.vitaminD
      if (nut.sodium != null) sodium = (sodium ?? 0) + nut.sodium
    }
  }
  return {
    calories,
    protein,
    carbs,
    fibre,
    calcium,
    iron,
    hydration,
    zinc: zinc ?? null,
    magnesium: magnesium ?? null,
    omega3: omega3 ?? null,
    vitaminD: vitaminD ?? null,
    sodium: sodium ?? null,
  }
}

/** Whether the FSANZ layer has any foods (for showing "Search database" in UI). */
export function isFSANZAvailable(): boolean {
  return getFoods().length > 0
}

/** One or more foods for a suggested meal (composite = multiple). */
type SuggestedLookupItem = { foodId: string; defaultGrams: number }

const SUGGESTED_MEAL_LOOKUP: Record<string, SuggestedLookupItem | SuggestedLookupItem[]> = {
  // --- Breakfast ---
  'Small bowl of oatmeal with berries and a drizzle of honey': [
    { foodId: 'f-3', defaultGrams: 80 },   // rolled oats
    { foodId: 'f-18', defaultGrams: 80 },  // blueberries
    { foodId: 'f-17', defaultGrams: 15 },  // honey
  ],
  'Whole grain toast with avocado': [
    { foodId: 'f-2', defaultGrams: 50 },   // wholemeal bread
    { foodId: 'f-11', defaultGrams: 80 },  // avocado
  ],
  'Greek yogurt (150g) with granola and sliced fruit': [
    { foodId: 'f-36', defaultGrams: 150 }, // Greek yoghurt, full fat
    { foodId: 'f-16', defaultGrams: 40 },  // granola / toasted muesli
    { foodId: 'f-1', defaultGrams: 80 },   // banana
  ],
  'Smoothie: banana, berries, milk or plant milk, small scoop of protein powder': [
    { foodId: 'f-1', defaultGrams: 100 },  // banana
    { foodId: 'f-18', defaultGrams: 80 },  // blueberries
    { foodId: 'f-5', defaultGrams: 200 },  // whole milk
  ],
  'Scrambled eggs on toast with avocado': [
    { foodId: 'f-4', defaultGrams: 100 },  // eggs
    { foodId: 'f-2', defaultGrams: 40 },   // wholemeal bread
    { foodId: 'f-11', defaultGrams: 50 },  // avocado
  ],
  'Scrambled eggs with avocado and cherry tomatoes': [
    { foodId: 'f-4', defaultGrams: 100 },  // eggs
    { foodId: 'f-11', defaultGrams: 50 },  // avocado
    { foodId: 'f-20', defaultGrams: 80 },  // cherry tomatoes
  ],
  'Rice cakes with avocado and sliced tomato': [
    { foodId: 'f-14', defaultGrams: 30 },  // rice cakes
    { foodId: 'f-11', defaultGrams: 80 },  // avocado
    { foodId: 'f-20', defaultGrams: 80 },  // cherry tomatoes
  ],
  'Weet-Bix with banana and milk': [
    { foodId: 'f-56', defaultGrams: 50 },  // Weet-Bix (~3 biscuits)
    { foodId: 'f-1', defaultGrams: 100 },  // banana
    { foodId: 'f-5', defaultGrams: 200 },  // whole milk
  ],
  'Bircher muesli: rolled oats soaked with grated apple and yoghurt': [
    { foodId: 'f-3', defaultGrams: 60 },   // rolled oats
    { foodId: 'f-9', defaultGrams: 100 },  // apple
    { foodId: 'f-8', defaultGrams: 100 },  // plain yoghurt
  ],
  'Milo with milk and Weet-Bix': [
    { foodId: 'f-74', defaultGrams: 25 },  // Milo powder (~3 tsp)
    { foodId: 'f-34', defaultGrams: 200 }, // reduced fat milk
    { foodId: 'f-56', defaultGrams: 35 },  // Weet-Bix (~2 biscuits)
  ],
  'Porridge with peanut butter, honey, and banana': [
    { foodId: 'f-58', defaultGrams: 80 },  // porridge, cooked
    { foodId: 'f-10', defaultGrams: 20 },  // peanut butter
    { foodId: 'f-17', defaultGrams: 15 },  // honey
    { foodId: 'f-1', defaultGrams: 100 },  // banana
  ],
  // --- Lunch ---
  'Turkey and cheese sandwich on whole grain bread with veggie sticks': [
    { foodId: 'f-2', defaultGrams: 80 },   // wholemeal bread
    { foodId: 'f-30', defaultGrams: 60 },  // turkey breast
    { foodId: 'f-32', defaultGrams: 20 },  // cheddar cheese
    { foodId: 'f-22', defaultGrams: 80 },  // carrot
  ],
  'Hummus with whole grain crackers and vegetables': [
    { foodId: 'f-39', defaultGrams: 80 },  // hummus
    { foodId: 'f-47', defaultGrams: 40 },  // wholegrain crackers
    { foodId: 'f-22', defaultGrams: 80 },  // carrot
  ],
  'Whole wheat pasta with lean meat sauce, vegetables, glass of milk': [
    { foodId: 'f-13', defaultGrams: 180 }, // wholemeal pasta, cooked
    { foodId: 'f-29', defaultGrams: 100 }, // beef mince, lean
    { foodId: 'f-5', defaultGrams: 200 },  // whole milk
  ],
  'Chicken salad wrap with fruit': [
    { foodId: 'f-15', defaultGrams: 60 },  // wheat wrap
    { foodId: 'f-7', defaultGrams: 80 },   // chicken breast
    { foodId: 'f-9', defaultGrams: 120 },  // apple
  ],
  'Tuna and rice with steamed greens': [
    { foodId: 'f-27', defaultGrams: 95 },  // canned tuna
    { foodId: 'f-6', defaultGrams: 150 },  // white rice, cooked
    { foodId: 'f-21', defaultGrams: 80 },  // broccoli
  ],
  'Brown rice bowl with chickpeas, roasted vegetables, and lemon-tahini dressing': [
    { foodId: 'f-12', defaultGrams: 150 }, // brown rice, cooked
    { foodId: 'f-24', defaultGrams: 100 }, // chickpeas
    { foodId: 'f-23', defaultGrams: 80 },  // capsicum
    { foodId: 'f-40', defaultGrams: 15 },  // tahini
  ],
  'Jacket sweet potato with cottage cheese and salad greens': [
    { foodId: 'f-50', defaultGrams: 180 }, // sweet potato, baked
    { foodId: 'f-62', defaultGrams: 80 },  // cottage cheese
    { foodId: 'f-66', defaultGrams: 40 },  // salad greens
  ],
  'Lentil and vegetable soup with a bread roll': [
    { foodId: 'f-25', defaultGrams: 100 }, // red lentils, cooked
    { foodId: 'f-22', defaultGrams: 60 },  // carrot
    { foodId: 'f-21', defaultGrams: 60 },  // broccoli
    { foodId: 'f-2', defaultGrams: 50 },   // wholemeal bread (roll)
  ],
  'Salmon and rice bowl with cucumber and edamame': [
    { foodId: 'f-60', defaultGrams: 95 },  // salmon, canned
    { foodId: 'f-6', defaultGrams: 150 },  // white rice, cooked
    { foodId: 'f-65', defaultGrams: 60 },  // cucumber
    { foodId: 'f-63', defaultGrams: 60 },  // edamame
  ],
  // --- Dinner ---
  'Grilled chicken breast with brown rice, roasted vegetables, side salad': [
    { foodId: 'f-7', defaultGrams: 120 },  // chicken breast
    { foodId: 'f-12', defaultGrams: 150 }, // brown rice, cooked
    { foodId: 'f-21', defaultGrams: 80 },  // broccoli
    { foodId: 'f-22', defaultGrams: 60 },  // carrot
  ],
  'Salmon with quinoa, steamed broccoli, whole grain roll': [
    { foodId: 'f-28', defaultGrams: 120 }, // salmon, grilled
    { foodId: 'f-46', defaultGrams: 150 }, // quinoa, cooked
    { foodId: 'f-21', defaultGrams: 80 },  // broccoli
    { foodId: 'f-2', defaultGrams: 40 },   // wholemeal bread (roll)
  ],
  'Burrito bowl: brown rice, black beans, grilled chicken, peppers, salsa': [
    { foodId: 'f-12', defaultGrams: 150 }, // brown rice, cooked
    { foodId: 'f-26', defaultGrams: 100 }, // kidney beans
    { foodId: 'f-7', defaultGrams: 100 },  // chicken breast
    { foodId: 'f-23', defaultGrams: 80 },  // capsicum (peppers)
  ],
  'Lentil curry with rice': [
    { foodId: 'f-25', defaultGrams: 150 }, // red lentils, cooked
    { foodId: 'f-6', defaultGrams: 150 },  // white rice, cooked
  ],
  'Whole wheat pasta with tomato-based sauce, vegetables, parmesan': [
    { foodId: 'f-13', defaultGrams: 200 }, // wholemeal pasta, cooked
    { foodId: 'f-20', defaultGrams: 100 }, // tomatoes
    { foodId: 'f-33', defaultGrams: 15 },  // parmesan
  ],
  'Tofu and vegetable stir-fry with brown rice': [
    { foodId: 'f-31', defaultGrams: 150 }, // firm tofu
    { foodId: 'f-21', defaultGrams: 80 },  // broccoli
    { foodId: 'f-23', defaultGrams: 80 },  // capsicum
    { foodId: 'f-12', defaultGrams: 150 }, // brown rice, cooked
  ],
  'Beef and vegetable stir-fry with brown rice': [
    { foodId: 'f-61', defaultGrams: 100 }, // beef steak, lean
    { foodId: 'f-12', defaultGrams: 150 }, // brown rice, cooked
    { foodId: 'f-23', defaultGrams: 80 },  // capsicum
    { foodId: 'f-69', defaultGrams: 80 },  // zucchini
  ],
  'Sweet potato and chickpea curry with rice': [
    { foodId: 'f-50', defaultGrams: 150 }, // sweet potato, baked
    { foodId: 'f-24', defaultGrams: 120 }, // chickpeas, canned
    { foodId: 'f-6', defaultGrams: 150 },  // white rice, cooked
  ],
  'Baked salmon with sweet potato and steamed peas': [
    { foodId: 'f-28', defaultGrams: 120 }, // salmon, grilled
    { foodId: 'f-50', defaultGrams: 150 }, // sweet potato, baked
    { foodId: 'f-68', defaultGrams: 80 },  // peas
  ],
  // --- Pre-training snacks ---
  'Banana with 1 tablespoon peanut or almond butter': [
    { foodId: 'f-1', defaultGrams: 100 },  // banana
    { foodId: 'f-10', defaultGrams: 20 },  // peanut butter
  ],
  'Small bowl of oatmeal with berries and honey': [
    { foodId: 'f-3', defaultGrams: 80 },   // rolled oats
    { foodId: 'f-18', defaultGrams: 80 },  // blueberries
    { foodId: 'f-17', defaultGrams: 15 },  // honey
  ],
  'Rice cakes (2) with hummus': [
    { foodId: 'f-14', defaultGrams: 30 },  // rice cakes
    { foodId: 'f-39', defaultGrams: 50 },  // hummus
  ],
  'Toast (2 slices) with honey and a glass of juice': [
    { foodId: 'f-2', defaultGrams: 60 },   // wholemeal bread
    { foodId: 'f-17', defaultGrams: 20 },  // honey
    { foodId: 'f-41', defaultGrams: 200 }, // orange juice
  ],
  '2–4 Medjool dates + sports drink or juice': [
    { foodId: 'f-45', defaultGrams: 60 },  // Medjool dates
    { foodId: 'f-42', defaultGrams: 400 }, // sports drink
  ],
  'Muesli bar and a piece of fruit': [
    { foodId: 'f-75', defaultGrams: 35 },  // muesli bar (~1 bar)
    { foodId: 'f-9', defaultGrams: 120 },  // apple
  ],
  'Crackers with cottage cheese and sliced cucumber': [
    { foodId: 'f-47', defaultGrams: 30 },  // wholegrain crackers
    { foodId: 'f-62', defaultGrams: 80 },  // cottage cheese
    { foodId: 'f-65', defaultGrams: 60 },  // cucumber
  ],
  // --- During training ---
  'Water (150–250 mL every 15–20 minutes)': { foodId: 'f-43', defaultGrams: 250 },
  'Sports drink (500–600 mL per hour) for longer sessions': { foodId: 'f-42', defaultGrams: 500 },
  'Fruit pouch (15g CHO) every 30–40 minutes': { foodId: 'f-9', defaultGrams: 120 }, // apple as proxy
  'Banana (about 20g CHO)': { foodId: 'f-1', defaultGrams: 100 },
  'Small handful of dried fruit (sultanas, dates)': [
    { foodId: 'f-44', defaultGrams: 30 },  // sultanas
    { foodId: 'f-45', defaultGrams: 20 },  // Medjool dates
  ],
  // --- Recovery snacks ---
  'Chocolate milk (250–500 mL) + piece of fruit': [
    { foodId: 'f-35', defaultGrams: 350 }, // chocolate milk
    { foodId: 'f-1', defaultGrams: 100 },  // banana
  ],
  'Yogurt bowl with granola and berries': [
    { foodId: 'f-8', defaultGrams: 150 },  // plain yoghurt
    { foodId: 'f-16', defaultGrams: 40 },  // granola
    { foodId: 'f-18', defaultGrams: 80 },  // blueberries
  ],
  'Peanut butter and banana sandwich': [
    { foodId: 'f-2', defaultGrams: 60 },   // wholemeal bread
    { foodId: 'f-10', defaultGrams: 15 },  // peanut butter
    { foodId: 'f-1', defaultGrams: 80 },   // banana
  ],
  'Chocolate milk (500 mL) + banana + handful of pretzels or crackers': [
    { foodId: 'f-35', defaultGrams: 500 }, // chocolate milk
    { foodId: 'f-1', defaultGrams: 100 },  // banana
    { foodId: 'f-47', defaultGrams: 30 },  // wholegrain crackers
  ],
  'Greek yogurt with honey, berries, and granola': [
    { foodId: 'f-8', defaultGrams: 150 },  // plain yoghurt
    { foodId: 'f-17', defaultGrams: 15 },  // honey
    { foodId: 'f-18', defaultGrams: 80 },  // blueberries
    { foodId: 'f-16', defaultGrams: 40 },  // granola
  ],
  'Chicken wrap: whole wheat tortilla, grilled chicken, vegetables, avocado + fruit': [
    { foodId: 'f-15', defaultGrams: 60 },  // wheat wrap
    { foodId: 'f-7', defaultGrams: 100 },  // chicken breast
    { foodId: 'f-11', defaultGrams: 50 },  // avocado
    { foodId: 'f-9', defaultGrams: 100 },  // apple
  ],
  'Banana, rice cakes, and a glass of plant-based milk': [
    { foodId: 'f-1', defaultGrams: 100 },  // banana
    { foodId: 'f-14', defaultGrams: 30 },  // rice cakes
    { foodId: 'f-38', defaultGrams: 250 }, // oat milk
  ],
  'Milo with milk and banana': [
    { foodId: 'f-74', defaultGrams: 25 },  // Milo powder (~3 tsp)
    { foodId: 'f-34', defaultGrams: 250 }, // reduced fat milk
    { foodId: 'f-1', defaultGrams: 100 },  // banana
  ],
  'Tuna on rice cakes with sliced tomato': [
    { foodId: 'f-27', defaultGrams: 95 },  // tuna, canned
    { foodId: 'f-14', defaultGrams: 30 },  // rice cakes
    { foodId: 'f-20', defaultGrams: 80 },  // cherry tomatoes
  ],
  'Cottage cheese with fruit and crackers': [
    { foodId: 'f-62', defaultGrams: 100 }, // cottage cheese
    { foodId: 'f-9', defaultGrams: 100 },  // apple
    { foodId: 'f-47', defaultGrams: 30 },  // wholegrain crackers
  ],
  'Yogurt with grapes': [
    { foodId: 'f-8', defaultGrams: 150 },  // plain yoghurt
    { foodId: 'f-70', defaultGrams: 80 },  // grapes
  ],
  // --- Legacy / individual food entries ---
  'Rice, white, long-grain, cooked, unenriched': { foodId: 'f-6', defaultGrams: 150 },
  'Milk, cow, regular, whole, pasteurised': { foodId: 'f-5', defaultGrams: 250 },
}

/**
 * If this suggested meal (exact name from MEAL_EXAMPLES) has a known DB match,
 * return one or more foods + default grams so the UI can link it for nutrients.
 */
export function getLookupForSuggestedMeal(
  mealName: string
): { foodId: string; defaultGrams: number }[] | null {
  const key = mealName.trim()
  if (!key) return null
  const raw = SUGGESTED_MEAL_LOOKUP[key]
  if (!raw) return null
  return Array.isArray(raw) ? raw : [raw]
}
